import { toThousands } from "@/utils/common";
import locale from "@/helpers/locale";
import moment from "moment";

// PayPay 列の prop 一覧。DEV-14574（営業日報詳細 PayPay 列 動的表示切替）
const PAYPAY_PROPS = ["paypay", "todayPayPay", "beforePayPay", "payPayNext"];

// hasPayPay=false のとき PayPay 列を表頭ツリーから再帰的に除外する。DEV-14574。
// subItems / subChildItems を再帰し、prop が PAYPAY_PROPS に含まれる列を取り除く。
function filterPayPayColumns(columns) {
  return columns
    .map(col => {
      const filtered = { ...col };
      if (filtered.subItems) {
        filtered.subItems = filterPayPayColumns(filtered.subItems);
      }
      if (filtered.subChildItems) {
        filtered.subChildItems = filterPayPayColumns(filtered.subChildItems);
      }
      return filtered;
    })
    .filter(col => !PAYPAY_PROPS.includes(col.prop));
}

// hasPayPay=false 时，从 CSV 导出的数据行中递归剔除 PayPay 的值。DEV-14700。
// 表头已通过 filterPayPayColumns 剔除，但数据行是用 Object.values 按位置展开的，
// 若 PayPay 值残留会导致列与表头错位（PayPay 数据混入）。这里用与表头相同的 PAYPAY_PROPS 把数据行也对齐。
// 为避免破坏原数据（页面展示用），递归重建对象后返回。
function stripPayPayKeys(value) {
  if (Array.isArray(value)) {
    return value.map(stripPayPayKeys);
  }
  if (value && typeof value === "object") {
    const result = {};
    Object.keys(value).forEach(key => {
      if (PAYPAY_PROPS.includes(key)) { return; }
      result[key] = stripPayPayKeys(value[key]);
    });
    return result;
  }
  return value;
}
export function filterPayPayData(rows) {
  return rows.map(stripPayPayKeys);
}

// 统计表头树中叶子节点（即实际 CSV 列）的数量。DEV-14700。
// 用于根据 PayPay 是否存在，正确计算 todayTicket 的空列分隔位置(pos)。
export function countLeafColumns(columns) {
  return columns.reduce((sum, col) => {
    if (col.subItems) { return sum + countLeafColumns(col.subItems); }
    if (col.subChildItems) { return sum + countLeafColumns(col.subChildItems); }
    return sum + 1;
  }, 0);
}

export const priceFormat = (row, column, cellValue) => {
  return toThousands(cellValue) + "円";
};

export const summaryTableHeader = () => [
  {
    label: locale("dailyDetail.monthMobilization"),
    prop: "visitor",
  },
  {
    label: locale("dailyDetail.monthBoxOffice"),
    prop: "total",
    formatter: priceFormat,
  }
];

const subItem1 = [
  {
    label: locale("dailyDetail.exceptedNumber"),
    prop: "visitor",
    width: 74,
    align: "right"
  },
  {
    label: locale("accountPayable.income"),
    prop: "total",
    width: 100,
    formatter: priceFormat,
    align: "right"
  },
];

const subItem2 = [
  {
    label: locale("dailyDetail.number"),
    prop: "visitor",
    width: 60,
  },
  {
    label: locale("cashierReport.dialog.th.amount"),
    prop: "total",
    width: 90,
    formatter: priceFormat,
  },
];
const subItem3 = [
  {
    label: locale("dailyDetail.seatNumber"),
    prop: "visitor",
    width: 60,
  },
  {
    label: locale("global.amount"),
    prop: "total",
    width: 90,
    formatter: priceFormat
  },
];
// ticketing
const _ticketTableHeader = () => [
  {
    label: locale("global.titleOfWork"),
    prop: "filmName",
    width: 200,
    filters: "filmName",
  },
  {
    label: locale("global.screenStandard"),
    prop: "version",
    width: 80,
    filters: "version",
  },
  {
    label: locale("trans.report.version.caption"),
    prop: "subtitlesVersion",
    width: 80,
    filters: "subtitlesVersion"
  },
  {
    label: locale("global.otherVersions"),
    prop: "otherVersion",
    width: 160,
    filters: "otherVersion"
  },
  {
    label: locale("movie.mainBusiness"),
    prop: "scheduleSalesGroup.boxOffice",
    subItems: subItem1,
  },
  {
    label: locale("movie.morningShow"),
    prop: "scheduleSalesGroup.morningShow",
    subItems: subItem1,
  },
  {
    label: locale("movie.lateShow"),
    prop: "scheduleSalesGroup.lateShow",
    subItems: subItem1,
  },
  {
    label: locale("movie.allNight"),
    prop: "scheduleSalesGroup.allNight",
    subItems: subItem1,
  },
  {
    label: locale("movie.specialBusiness"),
    prop: "scheduleSalesGroup.special",
    subItems: subItem1,
  },
  {
    label: locale("accPayMDetail.total"),
    prop: "scheduleSalesGroup.accumulated",
    subItems: subItem1,
    width: 140,
    formatter: priceFormat,
  },
  {
    label: locale("dailyDetail.specialSeatSalesBeforeRefund"),
    prop: "seatPrice.before",
    subItems: subItem3,
    width: 140,
    formatter: priceFormat,
  },
  {
    label: locale("dailyDetail.specialSeatSalesAfterRefund"),
    prop: "seatPrice.after",
    subItems: subItem3,
    width: 140,
    formatter: priceFormat,
  },
  {
    label: locale("global.window"),
    prop: "officeBox",
    subItems: [
      {
        label: locale("setting.pay.type.cash"),
        prop: "todayCash",
        subChildItems: subItem2,
      },
      {
        label: locale("dailyDetail.credit"),
        prop: "todayCredit",
        subChildItems: subItem2,
      },
      {
        label: "PayPay",
        prop: "todayPayPay",
        subChildItems: subItem2,
      },
      {
        label: locale("dailyDetail.beforehand.cash"),
        prop: "beforeCash",
        subChildItems: subItem2,
      },
      {
        label: locale("dailyDetail.beforehand.credit"),
        prop: "beforeCredit",
        subChildItems: subItem2,
      },
      {
        label: "事前（PayPay）",
        prop: "beforePayPay",
        subChildItems: subItem2,
      },
    ],
  },
  {
    label: locale("global.online"),
    prop: "online",
    subItems: [
      {
        label: locale("dailyDetail.today"),
        prop: "today",
        subChildItems: subItem2,
      },
      {
        label: locale("dailyDetail.before"),
        prop: "before",
        subChildItems: subItem2,
      },
    ],
  },
  {
    label: locale("cashierReport.dialog.advanceCoupon"),
    prop: "advanceTicket",
    subItems: [
      {
        label: locale("dailyDetail.today"),
        prop: "today",
        subChildItems: subItem2,
      },
      {
        label: locale("dailyDetail.before"),
        prop: "before",
        subChildItems: subItem2,
      },
    ],
  },
  {
    label: locale("cashierReport.dialog.nationalTicket"),
    prop: "nationalTicket",
    subItems: [
      {
        label: locale("dailyDetail.today"),
        prop: "today",
        subChildItems: subItem2,
      },
      {
        label: locale("dailyDetail.before"),
        prop: "before",
        subChildItems: subItem2,
      },
    ],
  },
  {
    label: locale("dailyDetail.couponAccountReceivable"),
    prop: "coupon",
    width: 160,
    subItems: [
      {
        label: locale("dailyDetail.today"),
        prop: "today",
        subChildItems: subItem2,
      },
      {
        label: locale("dailyDetail.before"),
        prop: "before",
        subChildItems: subItem2,
      },
    ],
  },
];
export const ticketTableHeader = (hasPayPay = true) => {
  const headers = _ticketTableHeader();
  return hasPayPay ? headers : filterPayPayColumns(headers);
};

// 招待券
export const invitationTableHeader = () => [
  {
    label: locale("couponLib.invitationTicket"),
    prop: "invitation",
    subItems: [
      {
        label: locale("dailyDetail.today"),
        prop: "today",
        subChildItems: subItem2,
      },
      {
        label: locale("dailyDetail.before"),
        prop: "before",
        subChildItems: subItem2,
      },
    ],
  },
];
// 先行販売
const _preSaleTableHeader = () => [
  {
    label: locale("global.titleOfWork"),
    prop: "filmName",
    width: 260,
    filters: "filmName",
  },
  {
    label: locale("global.screenStandard"),
    prop: "version",
    width: 140,
    filters: "version",
  },
  {
    label: locale("trans.report.version.caption"),
    prop: "subtitlesVersion",
    width: 140,
    filters: "subtitlesVersion",
  },
  {
    label: locale("global.otherVersions"),
    prop: "otherVersion",
    width: 160,
    filters: "otherVersion",
  },
  {
    label: [locale("dailyDetail.preSale"), locale("dailyDetail.show")],
    prop: "preSale",
    type: "dynamic",
    subItems: [
      {
        label: locale("accountReceivable.autoCash"),
        prop: "cashNext",
        subChildItems: subItem2,
      },
      {
        label: locale("dailyDetail.windowCredit"),
        prop: "creditNext",
        subChildItems: subItem2,
      },
      {
        label: "PayPay",
        prop: "payPayNext",
        subChildItems: subItem2,
      },
      {
        label: locale("dailyDetail.net"),
        prop: "onlineNext",
        subChildItems: subItem2,
      },
      {
        label: locale("dailyDetail.theaterAdvance"),
        prop: "advanceTicketNext",
        subChildItems: subItem2,
      },
      {
        label: locale("dailyDetail.commonCoupon"),
        prop: "nationalTicketNext",
        subChildItems: subItem2,
      },
      {
        label: locale("dailyDetail.couponAccountReceivable"),
        prop: "couponNext",
        width: 500,
        subChildItems: subItem2,
      },
      {
        label: locale("couponLib.invitationTicket"),
        prop: "invitationNext",
        subChildItems: subItem2,
      },
    ],
  },
];
export const preSaleTableHeader = (hasPayPay = true) => {
  const headers = _preSaleTableHeader();
  return hasPayPay ? headers : filterPayPayColumns(headers);
};

// 支払方法
export const paymentMethodTableHeader = () => [
  {
    label: locale("trans.report.ticketing.payMethod"),
    prop: "paymentName",
    width: 200,
  },
  {
    label: locale("cashierReport.dialog.th.cashQty"),
    prop: "visitor",
    align: "right",
    width: 50
  },
  {
    label: locale("dailyDetail.onCoupons"),
    prop: "total",
    formatter: priceFormat,
    width: 80,
    align: "right"
  },
  {
    label: locale("dailyDetail.seatSales"),
    prop: "seatTotal",
    formatter: priceFormat,
    width: 160,
    align: "right"
  },
];
// 支払方法（先売券の払戻)
export const paymentMethodRefundTableHeader = () => [
  {
    label: locale("dailyDetail.paymentRefund"),
    prop: "paymentName",
    width: 200,
  },
  {
    label: locale("cashierReport.dialog.th.cashQty"),
    prop: "visitor",
    align: "right",
    width: 50
  },
  {
    label: locale("dailyDetail.onCoupons"),
    prop: "total",
    formatter: priceFormat,
    width: 140,
    align: "right"
  },
  {
    label: locale("dailyDetail.seatSales"),
    prop: "seatTotal",
    formatter: priceFormat,
    width: 160,
    align: "right"
  },
];
// 売掛金
export const couponReceivableTableHeader = () => [
  {
    label: locale("accounting.accountsReceivable"),
    prop: "couponName",
    width: 140,
  },
  {
    label: locale("operation.NAT.qty"),
    prop: "visitor",
    width: 50,
    align: "right"
  },
  {
    label: locale("dailyDetail.onCoupons"),
    prop: "total",
    formatter: priceFormat,
    width: 80,
    align: "right"
  },
];
// 当日現金収支
export const dayCashSalesHeader = () => [
  {
    label: "当日現金収支",
    prop: "dayCashName",
    width: 140,
  },
  {
    label: locale("trans.other.price"),
    prop: "total",
    formatter: priceFormat,
    width: 100,
    align: "right"
  },
];
// Payment Ticket Items
export const paymentTicketItemsHeader = () => [
  {
    label: "支払方法（券種と物販）",
    prop: "paymentName",
    width: 180,
  },
  {
    label: "決済数",
    prop: "visitor",
    width: 100,
  },
  {
    label: "金額",
    prop: "total",
    width: 100,
    formatter: priceFormat,
    align: "right"
  },
];
// 売店
const _shopSalesTableHeader = () => [
  {
    label: locale("dailyDetail.shop.salesGroup"),
    prop: "salesGroup",
    width: 120,
  },
  {
    label: locale("dailyDetail.shop.dayTeight"),
    prop: "dayEight",
    align: "center",
    subItems: [
      {
        label: locale("accountReceivable.autoCash"),
        prop: "cash",
        formatter: priceFormat,
      },
      {
        label: locale("dailyDetail.windowCredit"),
        prop: "credit",
        formatter: priceFormat,
        width: 120,
      },
      {
        label: "PayPay",
        prop: "paypay",
        formatter: priceFormat,
      },
      {
        label: locale("dailyDetail.net"),
        prop: "online",
        formatter: priceFormat,
      },
    ],
  },
  {
    label: locale("dailyDetail.shop.dayTen"),
    prop: "dayTen",
    align: "center",
    subItems: [
      {
        label: locale("accountReceivable.autoCash"),
        prop: "cash",
        formatter: priceFormat,
      },
      {
        label: locale("dailyDetail.windowCredit"),
        prop: "credit",
        formatter: priceFormat,
        width: 120,
      },
      {
        label: "PayPay",
        prop: "paypay",
        formatter: priceFormat,
      },
      {
        label: locale("dailyDetail.net"),
        prop: "online",
        formatter: priceFormat,
      },
    ],
  },
  {
    label: locale("dailyDetail.shop.dayZero"),
    prop: "dayZero",
    align: "center",
    subItems: [
      {
        label: locale("accountReceivable.autoCash"),
        prop: "cash",
        formatter: priceFormat,
      },
      {
        label: locale("dailyDetail.windowCredit"),
        prop: "credit",
        formatter: priceFormat,
        width: 120,
      },
      {
        label: "PayPay",
        prop: "paypay",
        formatter: priceFormat,
      },
      {
        label: locale("dailyDetail.net"),
        prop: "online",
        formatter: priceFormat,
      },
    ],
  },
  {
    label: locale("dailyDetail.shop.dayNon"),
    prop: "dayNon",
    align: "center",
    subItems: [
      {
        label: locale("accountReceivable.autoCash"),
        prop: "cash",
        formatter: priceFormat,
      },
      {
        label: locale("dailyDetail.windowCredit"),
        prop: "credit",
        formatter: priceFormat,
        width: 120,
      },
      {
        label: "PayPay",
        prop: "paypay",
        formatter: priceFormat,
      },
      {
        label: locale("dailyDetail.net"),
        prop: "online",
        formatter: priceFormat,
      },
    ],
  },
  {
    label: locale("dailyDetail.shop.preSale"),
    prop: "dayBeforePreSale",
    // formatter: priceFormat,
    align: "center",
    subItems: [
      {
        label: locale("accountReceivable.autoCash"),
        prop: "cash",
        formatter: priceFormat,
      },
      {
        label: locale("dailyDetail.windowCredit"),
        prop: "credit",
        formatter: priceFormat,
        width: 120,
      },
      {
        label: "PayPay",
        prop: "paypay",
        formatter: priceFormat,
      },
      {
        label: locale("dailyDetail.net"),
        prop: "online",
        formatter: priceFormat,
      },
    ],
  },
  {
    label: locale("dailyDetail.shop.dayTotal"),
    prop: "dayTotal",
    formatter: priceFormat,
    align: "right"
  },
  {
    label: locale("dailyDetail.shop.advanceSalesTotal"),
    prop: "dayPreSaleTotal",
    // formatter: priceFormat,
    align: "center",
    subItems: [
      {
        label: locale("accountReceivable.autoCash"),
        prop: "cash",
        formatter: priceFormat,
      },
      {
        label: locale("dailyDetail.windowCredit"),
        prop: "credit",
        formatter: priceFormat,
        width: 120,
      },
      {
        label: "PayPay",
        prop: "paypay",
        formatter: priceFormat,
      },
      {
        label: locale("dailyDetail.net"),
        prop: "online",
        formatter: priceFormat,
      },
    ],
  },
  {
    label: locale("dailyDetail.shop.monthNon"),
    prop: "monthNon",
    formatter: priceFormat,
    align: "right"
  },
  {
    label: locale("dailyDetail.shop.monthZero"),
    prop: "monthZero",
    formatter: priceFormat,
    align: "right"
  },
  {
    label: locale("dailyDetail.shop.monthTeight"),
    prop: "monthEight",
    formatter: priceFormat,
    align: "right"
  },
  {
    label: locale("dailyDetail.shop.monthTen"),
    prop: "monthTen",
    formatter: priceFormat,
    align: "right"
  },
  {
    label: locale("dailyDetail.shop.monthTotal"),
    prop: "monthTotal",
    formatter: priceFormat,
    align: "right"
  },
];
export const shopSalesTableHeader = (hasPayPay = true) => {
  const headers = _shopSalesTableHeader();
  return hasPayPay ? headers : filterPayPayColumns(headers);
};

// 売店支払方法
export const shopPaymentTableHeader = () => [
  {
    label: locale("trans.report.ticketing.payMethod"),
    prop: "paymentName",
    width: 140,
  },
  {
    label: locale("dailyClosing.createDialog.otherCashCollectionRegistrationTable.th.qty"),
    prop: "visitor",
    width: 140,
    align: "right"
  },
  {
    label: locale("global.amount"),
    prop: "total",
    width: 140,
    formatter: priceFormat,
    align: "right"
  },
];
// 売店前日までの販売分の払戻
export const shopPaymentRefundTableHeader = () => [
  {
    label: locale("dailyDetail.shop.paymentRefund"),
    prop: "paymentName",
    width: 180,
  },
  {
    label: locale("dailyClosing.createDialog.otherCashCollectionRegistrationTable.th.qty"),
    prop: "visitor",
    width: 140,
    align: "right"
  },
  {
    label: locale("global.amount"),
    prop: "total",
    width: 140,
    formatter: priceFormat,
    align: "right"
  },
];

// その他の営業収入
// 売掛金現金回収
export const accountsRecoveryTableHeader = () => [
  {
    label: locale("accountPayable.requestName"),
    prop: "requestName",
    width: 180,
  },
  {
    label: locale("accountPayable.billingStatement"),
    prop: "requestDetails",
    width: 180,
  },
  {
    label: locale("dailyClosing.cashTableDialog.cashTypeTable.th.amount"),
    prop: "mount",
    width: 140,
    formatter: priceFormat,
  },
];
// その他営業収入現金回収
export const otherCashRecovery = () => [
  {
    label: locale("accountPayable.customerName"),
    prop: "clientName",
    width: 180,
  },
  {
    label: locale("accountPayable.itemName"),
    prop: "otherRevenueName",
    width: 180,
  },
  {
    label: locale("dailyClosing.cashTableDialog.cashTypeTable.th.amount"),
    prop: "cashRecovery",
    width: 140,
    formatter: priceFormat,
    align: "right"
  },
];
// 買掛金現金支払
export const accountsPaymentTableHeader = () => [
  {
    label: locale("accountPayable.requestName"),
    prop: "requestName",
    width: 180,
  },
  {
    label: locale("accountPayable.billingStatement"),
    prop: "requestDetails",
    width: 180,
  },
  {
    label: locale("dailyClosing.cashTableDialog.cashTypeTable.th.amount"),
    prop: "cashRecoveryAmount",
    width: 140,
    formatter: priceFormat,
    align: "right"
  },
];
// その他仕入現金支払
export const otherCashPaymentTableHeader = () => [
  {
    label: locale("accountPayable.customerName"),
    prop: "clientName",
    width: 180,
  },
  {
    label: locale("accountPayable.itemName"),
    prop: "otherGoodsName",
    width: 180,
  },
  {
    label: locale("dailyClosing.cashTableDialog.cashTypeTable.th.amount"),
    prop: "cashRecovery",
    width: 140,
    formatter: priceFormat,
    align: "right"
  },
];
// 売掛金-その他営業収入
export const salesAccountsTableHeader = () => [
  {
    label: locale("accountPayable.customerName"),
    prop: "clientName",
    width: 180,
  },
  {
    label: locale("accountPayable.itemName"),
    prop: "otherRevenueName",
    width: 180,
  },
  {
    label: locale("dailyClosing.cashTableDialog.cashTypeTable.th.amount"),
    prop: "cashRecovery",
    width: 140,
    formatter: priceFormat,
    align: "right"
  },
];
